import React, { useEffect, useMemo, useState, useRef } from "react";
import AdminLayout from "./Layout";
import { Link, useNavigate } from "react-router-dom";
import {
  downloadExcel,
  getAllUsersDashboard,
  getAllUsersNumbers,
  getAllUsersSummaryStats,
} from "../../apis";
import { useSelector } from "react-redux";
import { saveAs } from "file-saver";
import { formatDateForAPI } from "../../utils/dateUtils";
import communitiesData from "../../utils/communities_by_lga_ward.json";
import MultiSelect from "../../components/MultiSelect";
import * as nigerianStates from "nigerian-states-and-lgas";
import {
  COHORT_OPTIONS,
  ISSAM_COHORT_YEAR_FILTER_OPTIONS,
  ISSAM_YEAR1_COHORT_TAG,
} from "../../utils/cohorts";

/** Align JSON LGA slugs (e.g. sabon_gari) with nigerian-states-and-lgas labels (e.g. Sabon Gari). */
const normalizeLgaForCompare = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Helper function to extract date from MongoDB ObjectId
const getDateFromObjectId = (objectId) => {
  if (!objectId || typeof objectId !== 'string' || objectId.length < 8) return null;
  const timestamp = parseInt(objectId.substring(0, 8), 16);
  return new Date(timestamp * 1000);
};

// Format date for display
const formatDate = (date) => {
  if (!date) return '-';
  // Handle both Date objects and ISO date strings
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return '-';
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const AdminUsersList = () => {
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const canAddUser = !["attendance", "camp-checkin"].includes(
    auth?.userInfo?.role
  );
  const scopedAdminStateRaw = auth?.userInfo?.adminState;
  const scopedAdminState =
    typeof scopedAdminStateRaw === "string" ? scopedAdminStateRaw.trim() : "";
  const isAdminStateScoped = Boolean(scopedAdminState);

  // State Management
  const [users, setUsers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [filteredUsers, setFilteredUsers] = useState(0);
  const [mealData, setMealData] = useState(null);
  const [mealLoading, setMealLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  });

    // Comprehensive Filters
  const [filters, setFilters] = useState({
    disability: "",
    gender: "",
    idpClassification: "",
    state: "",
    lga: [], // Changed to array for multi-select
    ward: "",
    community: "",
    religion: "",
    physicalFitness: "",
    mspType: "",
    cohortTag: "",
    issamCohortYear: "",
  });

  const dropdownRefs = useRef({});

  // Search and Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("names");

  // State/LGA options sourced from the same JSON used in NameRegistration
  const states = ["Nasarawa", "Kaduna", "FCT"];
  const jsonLgas = communitiesData.map((item) => item.lga);
  const uniqueJsonLgas = useMemo(
    () =>
      Array.from(new Set(jsonLgas))
        .filter(Boolean)
        .sort((a, b) =>
          String(a).localeCompare(String(b), undefined, { sensitivity: "base" })
        ),
    [jsonLgas]
  );

  const availableLgaOptions = useMemo(() => {
    const selectedState = String(filters.state || "").trim();
    if (!selectedState) return [];

    const selectedStateLower = selectedState.toLowerCase();
    if (!["nasarawa", "kaduna"].includes(selectedStateLower)) return [];

    // Use the package for state->LGA mapping, but keep only LGAs present in our JSON
    // (so ward/community mapping continues to work).
    const libStateName = selectedStateLower.includes("abuja")
      ? "Federal Capital Territory"
      : selectedStateLower === "kaduna"
        ? "Kaduna"
        : "Nasarawa";
    const allowedFromLib = (nigerianStates.lgas(libStateName) ?? []).map((lga) =>
      normalizeLgaForCompare(lga)
    );
    const allowedSet = new Set(allowedFromLib);

    return uniqueJsonLgas.filter((lga) =>
      allowedSet.has(normalizeLgaForCompare(lga))
    );
  }, [filters.state, uniqueJsonLgas]);



  // Date Range Filter State
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
    dateFilterType: "createdAt", // "createdAt" (registration date) or "updatedAt" (last updated)
  });

  // Summary Statistics
  const [summaryStats, setSummaryStats] = useState({
    total: 0,
    male: 0,
    female: 0,
    otherGender: 0,
    unspecifiedGender: 0,
    existingMsp: 0,
    mechanic: 0,
    newMsp: 0,
    unspecifiedMsp: 0,
    disability: 0,
  });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [campOverview, setCampOverview] = useState({
    checkedIn: 0,
    checkedOut: 0,
    decamped: 0,
    infants: 0,
  });

  // Helper functions for LGA/Ward/Community
  const getWardsForLga = (lga) => {
    const entry = communitiesData.find((item) => item.lga.toLowerCase() === lga.toLowerCase());
    if (!entry) return [];
    return entry.wards.map((ward) => ward.ward);
  };

  const getCommunitiesForLgaAndWard = (lga, wardName) => {
    const entry = communitiesData.find((item) => item.lga.toLowerCase() === lga.toLowerCase());
    if (!entry) return [];

    const ward = entry.wards.find((w) => w.ward.toLowerCase() === wardName.toLowerCase());
    if (!ward) return [];

    const set = new Set();
    (ward.communities || []).forEach((community) => {
      if (community) {
        set.add(community);
      }
    });

    return Array.from(set).sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { sensitivity: "base" })
    );
  };

  const [availableWards, setAvailableWards] = useState([]);
  const [availableCommunities, setAvailableCommunities] = useState([]);

  // Sorting State
  const [sorting, setSorting] = useState({
    sortBy: null,
    sortOrder: "asc",
  });

  // Refs and Timeouts
  const searchTimeout = useRef(null);

  // Authentication Check
  useEffect(() => {
    if (!auth?.token) {
      navigate("/login");
      return;
    }
    if (auth?.userInfo?.role === "food-checker") {
      navigate("/food");
      return;
    }
    if (auth?.userInfo?.role === "test") {
      navigate("/test-scores");
    }
  }, [auth, navigate]);

  const toggleMenu = (userId, event) => {
    event.stopPropagation();
    setActiveMenu(activeMenu === userId ? null : userId);
  };

  // Fetch Users with Comprehensive Filtering
  const fetchUsers = async (page = 1) => {
    setIsSearching(true);
    try {
      // Prepare filter parameters
      const filterParams = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => {
          // For arrays, check if it has items
          if (Array.isArray(v)) {
            return v.length > 0;
          }
          // For strings, check if not empty
          return v !== "";
        })
      );

      // Convert LGA array to comma-separated string for API
      if (filterParams.lga && Array.isArray(filterParams.lga)) {
        filterParams.lga = filterParams.lga.join(",");
      }

      // Prepare search parameters
      const searchParams = searchTerm
        ? { term: searchTerm, type: searchType }
        : {};

      // Prepare date range parameters for API
      const dateParams = {};
      if (dateRange.startDate) {
        dateParams.startDate = dateRange.startDate;
      }
      if (dateRange.endDate) {
        dateParams.endDate = dateRange.endDate;
      }
      if (dateRange.startDate || dateRange.endDate) {
        dateParams.dateFilterType = dateRange.dateFilterType;
      }

      // Combine all parameters
      const params = {
        page,
        ...filterParams,
        ...searchParams,
        ...dateParams,
        ...(sorting.sortBy
          ? {
              sortBy: sorting.sortBy,
              sortOrder: sorting.sortOrder,
            }
          : {}),
      };

      const response = await getAllUsersNumbers(params);
      console.log("getUsersNumbers", response);

      // Update state - check both response.data and response.data.data structures
      const responseData = response?.data?.data || response?.data;
      const fetchedUsers = responseData?.users || [];
      
      setUsers(fetchedUsers);
      setPagination(responseData?.pagination || pagination);
      setFilteredUsers(responseData?.filteredUsers || 0);
    } catch (error) {
      console.error("Fetch users error:", error);
      setUsers([]);
      setFilteredUsers(0);
    } finally {
      setIsSearching(false);
    }
  };

  // Initial load on mount
  useEffect(() => {
    if (auth?.token) {
      fetchUsers(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.token]);

  // Update wards when LGA changes
  useEffect(() => {
    if (filters.lga && filters.lga.length > 0) {
      // Get wards from all selected LGAs
      const allWards = new Set();
      filters.lga.forEach(lga => {
        const wards = getWardsForLga(lga);
        wards.forEach(ward => allWards.add(ward));
      });
      setAvailableWards(Array.from(allWards));
      // Reset ward and community if current ward is not in any selected LGA
      if (filters.ward && !Array.from(allWards).includes(filters.ward)) {
        handleFilterChange("ward", "");
        handleFilterChange("community", "");
      }
    } else {
      setAvailableWards([]);
      setAvailableCommunities([]);
    }
  }, [filters.lga]);

  // Update communities when ward changes
  useEffect(() => {
    if (filters.lga && filters.lga.length > 0 && filters.ward) {
      // Get communities from all selected LGAs for the selected ward
      const allCommunities = new Set();
      filters.lga.forEach(lga => {
        const communities = getCommunitiesForLgaAndWard(lga, filters.ward);
        communities.forEach(community => allCommunities.add(community));
      });
      setAvailableCommunities(Array.from(allCommunities));
      // Reset community if it's not in the new list
      if (filters.community && !Array.from(allCommunities).some(c => c.toLowerCase() === filters.community.toLowerCase())) {
        handleFilterChange("community", "");
      }
    } else {
      setAvailableCommunities([]);
    }
  }, [filters.lga, filters.ward]);

  // Fetch summary statistics
  const fetchSummaryStats = async () => {
    setSummaryLoading(true);
    try {
      // Prepare filter parameters
      const filterParams = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => {
          // For arrays, check if it has items
          if (Array.isArray(v)) {
            return v.length > 0;
          }
          // For strings, check if not empty
          return v !== "";
        })
      );
      
      // Convert LGA array to comma-separated string for API
      if (filterParams.lga && Array.isArray(filterParams.lga)) {
        filterParams.lga = filterParams.lga.join(",");
      }

      // Fetch summary statistics from the API
      const params = {
        ...filterParams,
        ...(searchTerm ? { term: searchTerm } : {}),
      };

      const response = await getAllUsersSummaryStats(params);
      const responseData = response?.data?.data || response?.data;

      if (responseData) {
        setSummaryStats({
          total: responseData.total || 0,
          male: responseData.male || 0,
          female: responseData.female || 0,
          otherGender: responseData.otherGender || 0,
          unspecifiedGender: responseData.unspecifiedGender || 0,
          existingMsp: responseData.existingMsp || 0,
          mechanic: responseData.mechanic || 0,
          newMsp: responseData.newMsp || 0,
          unspecifiedMsp: responseData.unspecifiedMsp || 0,
          disability: responseData.disability || 0,
        });
      }
    } catch (error) {
      console.error("Fetch summary stats error:", error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchCampOverview = async () => {
    if (!auth?.token) return;
    setOverviewLoading(true);
    try {
      const dashboardParams = {
        date: formatDateForAPI(),
        cohortTag: filters.cohortTag || undefined,
        issamCohortYear: filters.issamCohortYear || undefined,
        state: filters.state || undefined,
        // Dashboard endpoint supports a single LGA; only pass when one is selected.
        lga:
          Array.isArray(filters.lga) && filters.lga.length === 1
            ? filters.lga[0]
            : undefined,
        community: filters.community || undefined,
        disability: filters.disability || undefined,
        gender: filters.gender || undefined,
        religion: filters.religion || undefined,
        mspType: filters.mspType || undefined,
      };

      const response = await getAllUsersDashboard(dashboardParams);
      if (response?.status === 200 && response?.data?.success) {
        const data = response.data.data || {};
        setCampOverview({
          checkedIn: data?.campStatus?.checkedIn || 0,
          checkedOut: data?.campStatus?.checkedOut || 0,
          decamped: data?.campStatus?.decamped || 0,
          infants: data?.infantsCount || 0,
        });
      } else {
        setCampOverview({ checkedIn: 0, checkedOut: 0, decamped: 0, infants: 0 });
      }
    } catch (error) {
      setCampOverview({ checkedIn: 0, checkedOut: 0, decamped: 0, infants: 0 });
    } finally {
      setOverviewLoading(false);
    }
  };

  // Fetch summary stats when filters change
  useEffect(() => {
    if (auth?.token) {
      fetchSummaryStats();
      fetchCampOverview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchTerm, auth?.token]);

  // Debounced Search
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      fetchUsers(currentPage);
    }, 300);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchTerm, searchType, currentPage, filters, sorting, dateRange]);

  // Sorting Handler
  const handleSort = (field) => {
    setSorting((prev) => ({
      sortBy: field,
      sortOrder:
        prev.sortBy === field && prev.sortOrder === "asc" ? "desc" : "asc",
    }));
  };

  // Filter Change Handler
  const handleFilterChange = (filterName, value) => {
    console.log({
      filterName, value
    });

    // Admin-state scoped users cannot change state from the UI.
    if (isAdminStateScoped && filterName === "state") return;

    setFilters((prev) => {
      const next = { ...prev, [filterName]: value };

      if (filterName === "cohortTag" && value) {
        next.issamCohortYear = "";
      }
      if (filterName === "issamCohortYear" && value) {
        next.cohortTag = "";
      }

      // Reset dependent fields when state or LGA changes to mirror NameRegistration behavior
      if (filterName === "state") {
        next.lga = [];
        next.ward = "";
        next.community = "";
      }

      if (filterName === "lga") {
        next.ward = "";
        next.community = "";
      }

      return next;
    });
    setCurrentPage(1);
  };

  // If adminState is set, force state filter and reset dependent location filters.
  useEffect(() => {
    if (!isAdminStateScoped) return;

    const scopedStateValue = scopedAdminState.toLowerCase();
    if (filters.state === scopedStateValue) return;

    setFilters((prev) => ({
      ...prev,
      state: scopedStateValue,
      lga: [],
      ward: "",
      community: "",
    }));
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminStateScoped, scopedAdminState]);

  // Drop LGAs that are outside the selected/scoped state.
  useEffect(() => {
    if (!filters.lga || filters.lga.length === 0) return;
    const allowedSet = new Set(availableLgaOptions.map((l) => String(l).toLowerCase()));
    const next = filters.lga.filter((lga) => allowedSet.has(String(lga).toLowerCase()));
    if (next.length === filters.lga.length) return;

    setFilters((prev) => ({
      ...prev,
      lga: next,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableLgaOptions]);

  // Handle multi-select LGA change
  const handleLgaMultiSelect = (selectedLgas) => {
    handleFilterChange("lga", selectedLgas);
  };

  // Render Sorting Indicator
  const renderSortIndicator = (field) => {
    if (sorting.sortBy !== field) return null;
    return sorting.sortOrder === "asc" ? " ▲" : " ▼";
  };

  const displayUsers = useMemo(() => {
    const normalize = (value) => String(value || "").trim().toLowerCase();
    const y1 = ISSAM_YEAR1_COHORT_TAG.toLowerCase();
    let list = users || [];

    const issam = String(filters.issamCohortYear || "").trim();
    if (issam === "1") {
      list = list.filter((u) => normalize(u?.cohortTag) === y1);
    } else if (issam === "2") {
      list = list.filter((u) => normalize(u?.cohortTag) !== y1);
    }

    // TCP cohort is applied server-side by getUsersNumbers; do not re-filter here
    // (avoids empty rows when DB cohort strings differ slightly from the select value).
    return list;
  }, [users, filters.issamCohortYear]);

  const handleExportExcel = async () => {
    try {
      setExcelLoading(true);
      // Prepare filter parameters
      const filterParams = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => {
          // For arrays, check if it has items
          if (Array.isArray(v)) {
            return v.length > 0;
          }
          // For strings, check if not empty
          return v !== "";
        })
      );
      
      // Convert LGA array to comma-separated string for API
      if (filterParams.lga && Array.isArray(filterParams.lga)) {
        filterParams.lga = filterParams.lga.join(",");
      }

      // Prepare search parameters
      const searchParams = searchTerm
        ? { term: searchTerm }
        : {};

      // Prepare date range parameters for export
      const dateParams = {};
      if (dateRange.startDate) {
        dateParams.startDate = dateRange.startDate;
      }
      if (dateRange.endDate) {
        dateParams.endDate = dateRange.endDate;
      }
      if (dateRange.startDate || dateRange.endDate) {
        dateParams.dateFilterType = dateRange.dateFilterType;
      }

      // Combine all parameters (exclude page for export)
      const params = {
        ...filterParams,
        registeredUsersOnly: "false",
        ...searchParams,
        ...dateParams,
        ...(sorting.sortBy
          ? {
              sortBy: sorting.sortBy,
              sortOrder: sorting.sortOrder,
            }
          : {}),
      };

      console.log("Export params:", params);
      const response = await downloadExcel(params);

      if (!response || response.status !== 200) {
        setExcelLoading(false);
        // Get server error details if available
        const errorMessage = response?.errorDetails?.message || 
                            response?.errorDetails?.error ||
                            "Export failed";
        console.error("Server error:", response?.errorDetails);
        throw new Error(errorMessage);
      }

      // Generate filename with date
      const date = formatDateForAPI();
      const filename = `all_users_export_${date}.xlsx`;

      // Create blob from response data
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Download the file
      saveAs(blob, filename);
      setExcelLoading(false);
    } catch (error) {
      console.error("Export error:", error);
      setExcelLoading(false);
      alert(error.message || "Failed to export Excel file. Please try again.");
    }
  };

  return (
    <AdminLayout>
      <div className="users-list">
        {/* Header Section */}
        <header className="users-list__header">
          <h2 className="users-list__title">All Users</h2>

          <div className="users-list__search-wrapper">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="users-list__search-input"
              style={{ minWidth: "260px" }}
            />
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="users-list__search-type"
            >
              <option value="names">Name</option>
              <option value="email">Email</option>
              <option value="phoneNumber">Phone</option>
              <option value="userId">User ID</option>
            </select>
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setCurrentPage(1);
                }}
                className="users-list__clear-search"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "5px 10px",
                  fontSize: "18px",
                  color: "#666",
                }}
                title="Clear search"
              >
                ×
              </button>
            )}
            <h2 className="users-list__registered">
              All usersss:{" "}
              {loading ? "Loading..." : <span>{filteredUsers}</span>}
            </h2>
            {canAddUser && (
              <Link to="/create-user" className="users-list__add-link">
                <button type="button" className="users-list__add-button">
                  Add User
                </button>
              </Link>
            )}
            <select
              value={filters.issamCohortYear}
              onChange={(e) => {
                handleFilterChange("issamCohortYear", e.target.value);
              }}
              className="users-list__search-type"
              title="ISSAM intake year (independent of TCP cohort list)"
            >
              <option value="">All ISSAM years</option>
              {ISSAM_COHORT_YEAR_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {!isAdminStateScoped && (
              <select
                value={filters?.state}
                onChange={(e) => {
                  handleFilterChange("state", e.target.value);
                }}
                className="users-list__search-type"
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state} value={state.toLowerCase()}>
                    {state}
                  </option>
                ))}
              </select>
            )}
            <div style={{ minWidth: "200px", maxWidth: "300px" }}>
              <MultiSelect
                options={availableLgaOptions}
                value={filters.lga || []}
                onChange={handleLgaMultiSelect}
                placeholder="Select LGA(s)..."
                searchPlaceholder="Search LGAs..."
                className="users-list__search-type"
                disabled={availableLgaOptions.length === 0}
              />
            </div>
            {filters.lga && availableWards.length > 0 && (
              <select
                value={filters.ward}
                onChange={(e) => {
                  handleFilterChange("ward", e.target.value);
                }}
                className="users-list__search-type"
              >
                <option value="">All Wards</option>
                {availableWards.map((ward) => (
                  <option key={ward} value={ward}>
                    {ward}
                  </option>
                ))}
              </select>
            )}
            {filters.ward && availableCommunities.length > 0 && (
              <select
                value={filters.community}
                onChange={(e) => {
                  handleFilterChange("community", e.target.value);
                }}
                className="users-list__search-type"
              >
                <option value="">All Communities</option>
                {availableCommunities.map((community) => (
                  <option key={community} value={community}>
                    {community}
                  </option>
                ))}
              </select>
            )}
            <select
              value={filters.mspType}
              onChange={(e) => {
                handleFilterChange("mspType", e.target.value);
              }}
              className="users-list__search-type"
            >
              <option value="">All MSP Types</option>
              <option value="new msp">New MSP</option>
              <option value="existing msp">Existing MSP</option>
              <option value="mechanic">Mechanic</option>
            </select>
            <select
              value={filters.religion}
              onChange={(e) => {
                handleFilterChange("religion", e.target.value);
              }}
              className="users-list__search-type"
            >
              <option value="">All Religions</option>
              <option value="christianity">Christianity</option>
              <option value="islam">Islam</option>
              {/* <option value="traditional">Traditional</option> */}
              <option value="other">Other</option>
            </select>
            <select
              value={filters.disability}
              onChange={(e) => {
                handleFilterChange("disability", e.target.value);
              }}
              className="users-list__search-type"
            >
              <option value="">All Disabilities</option>
              <option value="yes">With Disability</option>
              <option value="no">No Disability</option>
            </select>
            <select
              value={filters.idpClassification}
              onChange={(e) => {
                handleFilterChange("idpClassification", e.target.value);
              }}
              className="users-list__search-type"
            >
              <option value="">All IDP Classifications</option>
              <option value="idp">IDP</option>
              <option value="non-idp">Non-IDP</option>
            </select>
            {filters.issamCohortYear !== "1" && (
              <select
                value={filters.cohortTag}
                onChange={(e) => {
                  handleFilterChange("cohortTag", e.target.value);
                }}
                className="users-list__search-type"
                title="TCP cohort (hidden while ISSAM Year 1 is selected)"
              >
                <option value="">All Cohorts</option>
                {COHORT_OPTIONS.map((cohort) => (
                  <option key={cohort.value} value={cohort.value}>
                    {cohort.label}
                  </option>
                ))}
              </select>
            )}
          
          </div>
          
          {/* Date Range Filter */}
          <div className="users-list__date-range-filter" style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginTop: "15px",
            padding: "12px 16px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            flexWrap: "wrap"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <label style={{ fontSize: "13px", color: "#6c757d", fontWeight: "600" }}>Filter By:</label>
              <select
                value={dateRange.dateFilterType}
                onChange={(e) => {
                  setDateRange(prev => ({ ...prev, dateFilterType: e.target.value }));
                  setCurrentPage(1);
                }}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ced4da",
                  borderRadius: "6px",
                  fontSize: "14px",
                  outline: "none",
                  backgroundColor: "white",
                  cursor: "pointer",
                }}
              >
                <option value="createdAt">Registration Date</option>
                <option value="updatedAt">Last Updated</option>
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <label style={{ fontSize: "13px", color: "#6c757d" }}>From:</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => {
                  setDateRange(prev => ({ ...prev, startDate: e.target.value }));
                  setCurrentPage(1);
                }}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ced4da",
                  borderRadius: "6px",
                  fontSize: "14px",
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <label style={{ fontSize: "13px", color: "#6c757d" }}>To:</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => {
                  setDateRange(prev => ({ ...prev, endDate: e.target.value }));
                  setCurrentPage(1);
                }}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ced4da",
                  borderRadius: "6px",
                  fontSize: "14px",
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
              />
            </div>
            {(dateRange.startDate || dateRange.endDate) && (
              <button
                onClick={() => {
                  setDateRange(prev => ({ ...prev, startDate: "", endDate: "" }));
                  setCurrentPage(1);
                }}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: "500",
                  transition: "background-color 0.2s",
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = "#c82333"}
                onMouseOut={(e) => e.target.style.backgroundColor = "#dc3545"}
              >
                Clear Dates
              </button>
            )}
          </div>
        </header>

        {/* Overview Section */}
        <div style={{ 
          marginBottom: "30px", 
          padding: "20px", 
          backgroundColor: "#f5f5f5", 
          borderRadius: "8px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "15px"
        }}>
          <div style={{ 
            backgroundColor: "white", 
            padding: "15px", 
            borderRadius: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>Total (in scope)</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#333" }}>
              {summaryLoading ? "..." : summaryStats.total}
            </div>
          </div>
          <div style={{ 
            backgroundColor: "white", 
            padding: "15px", 
            borderRadius: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>Male</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#2196F3" }}>
              {summaryLoading ? "..." : summaryStats.male}
            </div>
          </div>
          <div style={{ 
            backgroundColor: "white", 
            padding: "15px", 
            borderRadius: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>Female</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#E91E63" }}>
              {summaryLoading ? "..." : summaryStats.female}
            </div>
          </div>
          <div style={{ 
            backgroundColor: "white", 
            padding: "15px", 
            borderRadius: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>Existing MSP</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#4CAF50" }}>
              {summaryLoading ? "..." : summaryStats.existingMsp}
            </div>
          </div>
          <div style={{ 
            backgroundColor: "white", 
            padding: "15px", 
            borderRadius: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>Mechanic</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#FF9800" }}>
              {summaryLoading ? "..." : summaryStats.mechanic}
            </div>
          </div>
          <div style={{ 
            backgroundColor: "white", 
            padding: "15px", 
            borderRadius: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>New MSP</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#9C27B0" }}>
              {summaryLoading ? "..." : summaryStats.newMsp}
            </div>
          </div>
          <div style={{ 
            backgroundColor: "white", 
            padding: "15px", 
            borderRadius: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>Unspecified MSP</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#607D8B" }}>
              {summaryLoading ? "..." : summaryStats.unspecifiedMsp}
            </div>
          </div>
          <div style={{ 
            backgroundColor: "white", 
            padding: "15px", 
            borderRadius: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>With Disability</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#F44336" }}>
              {summaryLoading ? "..." : summaryStats.disability}
            </div>
          </div>
          <div style={{ 
            backgroundColor: "white", 
            padding: "15px", 
            borderRadius: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>Checked In</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#28a745" }}>
              {overviewLoading ? "..." : campOverview.checkedIn}
            </div>
          </div>
          <div style={{ 
            backgroundColor: "white", 
            padding: "15px", 
            borderRadius: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>Checked Out</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#dc3545" }}>
              {overviewLoading ? "..." : campOverview.checkedOut}
            </div>
          </div>
          <div style={{ 
            backgroundColor: "white", 
            padding: "15px", 
            borderRadius: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>Decamped</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#6f42c1" }}>
              {overviewLoading ? "..." : campOverview.decamped}
            </div>
          </div>
          <div style={{ 
            backgroundColor: "white", 
            padding: "15px", 
            borderRadius: "6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>Infants</div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#0d6efd" }}>
              {overviewLoading ? "..." : campOverview.infants}
            </div>
          </div>
        </div>

        {!isAdminStateScoped && (
          <>
            <button
              onClick={handleExportExcel}
              className="users-list__add-button"
              style={{ marginLeft: "10px" }}
              disabled={excelLoading}
            >
              {excelLoading ? "Loading..." : "Export to Excel"}
            </button>
            <br />
            <br />
          </>
        )}
        <div className="users-list__table-container">
          {isSearching ? (
            <div className="users-list__loading">Searching...</div>
          ) : (
            <>
              <table className="users-list__table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("userId")}>
                      ID{renderSortIndicator("userId")}
                    </th>
                    <th onClick={() => handleSort("names")}>
                      Name{renderSortIndicator("names")}
                    </th>
                    <th onClick={() => handleSort("phoneNumber")}>
                      Phone{renderSortIndicator("phoneNumber")}
                    </th>
                    <th onClick={() => handleSort("email")}>
                      Email{renderSortIndicator("email")}
                    </th>
                    <th onClick={() => handleSort("age")}>
                      Age{renderSortIndicator("age")}
                    </th>
                    <th onClick={() => handleSort("createdAt")}>
                      Registered On{renderSortIndicator("createdAt")}
                    </th>
                    <th onClick={() => handleSort("updatedAt")}>
                      Last Updated{renderSortIndicator("updatedAt")}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayUsers?.map((user) => (
                    <tr key={user?._id}>
                      <td>{user?.userId}</td>
                      <td>{user?.names}</td>
                      <td>{user?.phoneNumber}</td>
                      <td>{user?.email}</td>
                      <td>{user?.age}</td>
                      <td>{user?.createdAt ? formatDate(user.createdAt) : formatDate(getDateFromObjectId(user?._id))}</td>
                      <td>{formatDate(user?.updatedAt)}</td>
                      <td>
                        <div className="users-list__actions">
                          <div
                            className="users-list__dropdown"
                            ref={(el) => (dropdownRefs.current[user._id] = el)}
                          >
                            <button
                              className="users-list__dropdown-trigger"
                              onClick={(e) => toggleMenu(user._id, e)}
                            >
                              •••
                            </button>
                            {activeMenu === user._id && (
                              <div className="users-list__dropdown-menu">
                                <Link
                                  to={`/admin/user/${user._id}`}
                                  className="users-list__dropdown-item"
                                >
                                  View User Details
                                </Link>
                                <Link
                                  to={`/admin/users/${user._id}`}
                                  className="users-list__dropdown-item"
                                >
                                  Update Personal Info
                                </Link>
                                <Link
                                  to={`/admin/users/image/${user._id}`}
                                  className="users-list__dropdown-item"
                                >
                                  Update Image
                                </Link>
                                <Link
                                  // to={`/admin/users/fingerprint/${user._id}`}
                                  to={`/update-finger-print`}
                                  className="users-list__dropdown-item"
                                >
                                  Update Fingerprint
                                </Link>
                                {auth?.userInfo?.role === "health" && <Link
                                  to={`/health/user/${user._id}`}
                                  className="users-list__dropdown-item"
                                >
                                  Record Health Appointment
                                </Link>}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(!displayUsers || displayUsers.length === 0) && (
                    <tr>
                      <td colSpan={8} style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                        No users match the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="users-list__pagination">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className="users-list__pagination-button"
                >
                  Previous
                </button>

                <span className="users-list__pagination-info">
                  Page {pagination?.currentPage} of {pagination?.totalPages}
                </span>

                <button
                  onClick={() =>
                    setCurrentPage((prev) =>
                      Math.min(pagination.totalPages, prev + 1)
                    )
                  }
                  disabled={currentPage === pagination?.totalPages}
                  className="users-list__pagination-button"
                >
                  Next
                </button>

                <span className="users-list__pagination-total">
                  Total Items: {pagination?.totalItems}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUsersList;
